import { useCallback } from "react";

import { APP_MODULES } from "@/data_source/constants";
import { ProjectSelectedStore } from "@/stores/project-selected-store";
import { UserStore } from "@/stores/user-store";
import { WorkspaceContextStore } from "@/stores/workspace-context-store";

import { useObservable } from "../hook";
import {
  hasCanonicalOperationalCapability,
  resolveCapabilityAccess,
  type CapabilityContractFallback
} from "./capabilities";
import {
  MODULE_PERMISSIONS,
  getGrantedPermissions,
  hasAnyLegacyPermission,
  matchesPermission,
  normalizePermissionPattern
} from "./permissions";

export type UserAuthAction = "create" | "read" | "update" | "delete";
type AppModuleName = (typeof APP_MODULES)[keyof typeof APP_MODULES];
type ModulePermissionValue =
  | (typeof MODULE_PERMISSIONS)[keyof typeof MODULE_PERMISSIONS]
  | "all"
  | "none";

export const UI_MODULE_PERMISSIONS: Partial<
  Record<AppModuleName, ModulePermissionValue>
> = {
  [APP_MODULES.DASHBOARD_MODULE]: MODULE_PERMISSIONS.DASHBOARD_MODULE,
  [APP_MODULES.WORK_ORDER_MODULE]: MODULE_PERMISSIONS.WORK_ORDER_MODULE,
  [APP_MODULES.ASSETS_MODULE]: MODULE_PERMISSIONS.ASSETS_MODULE,
  [APP_MODULES.INVENTORY_MODULE]: MODULE_PERMISSIONS.INVENTORY_MODULE,
  [APP_MODULES.FORM_MODULE]: MODULE_PERMISSIONS.FORM_MODULE,
  [APP_MODULES.FORM_AUDIT_MODULE]: MODULE_PERMISSIONS.FORM_AUDIT_MODULE,
  [APP_MODULES.SITE_VISIT_MODULE]: MODULE_PERMISSIONS.SITE_VISIT_MODULE,
  [APP_MODULES.LIBRARY_MODULE]: MODULE_PERMISSIONS.LIBRARY_MODULE,
  [APP_MODULES.MESSAGE_MODULE]: MODULE_PERMISSIONS.MESSAGE_MODULE,
  [APP_MODULES.QR_MODULE]: MODULE_PERMISSIONS.QR_MODULE,
  [APP_MODULES.ADMIN_ROLE_MODULE]: MODULE_PERMISSIONS.ADMIN_ROLE_MODULE,
  [APP_MODULES.TEAM_MODULE]: MODULE_PERMISSIONS.TEAM_MODULE,
  [APP_MODULES.WORKSPACE_MODULE]: MODULE_PERMISSIONS.WORKSPACE_MODULE,
  [APP_MODULES.SETTINGS_MODULE]: MODULE_PERMISSIONS.SETTINGS_MODULE,
  [APP_MODULES.REPORTS_MODULE]: MODULE_PERMISSIONS.REPORTS_MODULE,
  [APP_MODULES.DOCUMENTS_MODULE]: MODULE_PERMISSIONS.DOCUMENTS,
  [APP_MODULES.TIMESHEET_MODULE]: MODULE_PERMISSIONS.TIMESHEET_MODULE
};

function isPermissionForAction(
  permissionPattern: string,
  action: string
): boolean {
  const normalizedPermission = normalizePermissionPattern(
    permissionPattern
  ).replace(/\\\./g, ".");
  const separatorIndex = normalizedPermission.indexOf(".");
  const codename =
    separatorIndex === -1
      ? normalizedPermission
      : normalizedPermission.slice(separatorIndex + 1);
  const normalizedCodename = codename
    .replace(/^\(can_\)\?/, "")
    .replace(/^can_/, "");

  return (
    normalizedCodename === action || normalizedCodename.startsWith(`${action}_`)
  );
}

function extractModulePermissions(
  permissionConfig: ModulePermissionValue | undefined,
  permissionKey = "view"
): string[] | "all" | "none" {
  if (!permissionConfig || permissionConfig === "none") return "none";
  if (permissionConfig === "all") return "all";

  if (Array.isArray(permissionConfig)) {
    return permissionConfig.filter((permission) =>
      isPermissionForAction(permission, permissionKey)
    );
  }

  return Object.entries(permissionConfig).flatMap(([key, value]) => {
    if (typeof value === "string") {
      return key === permissionKey ? [value] : [];
    }

    const nestedPermissions = extractModulePermissions(value, permissionKey);
    return Array.isArray(nestedPermissions) ? nestedPermissions : [];
  });
}

export function useUserAuth() {
  // AppHome owns the sole `useUserMe()` subscription and publishes to UserStore.
  // Reading the store here avoids N parallel GET /api/users/me per mounted page.
  const [user] = useObservable(UserStore.getInstance().observable);
  const [workspace] = useObservable(
    ProjectSelectedStore.getInstance().observable
  );
  const [workspaceContext] = useObservable(
    WorkspaceContextStore.getInstance().observable
  );
  const isMasterContext = useCallback((): boolean => {
    if (workspaceContext) {
      return (
        workspaceContext.resolvedContextType === "master" &&
        workspaceContext.canAccessMaster
      );
    }

    return Boolean(workspace?.is_master);
  }, [workspace?.is_master, workspaceContext]);
  const isOperationalContext = useCallback((): boolean => {
    if (workspaceContext) {
      return (
        workspaceContext.resolvedContextType === "operational" &&
        workspace?.can_operate !== false
      );
    }

    return Boolean(workspace?.can_operate && !workspace?.is_master);
  }, [workspace?.can_operate, workspace?.is_master, workspaceContext]);
  const isModuleAuthorized = useCallback(
    (
      moduleNames: AppModuleName[],
      requireWorkspaces: ("master" | "operational")[] = ["operational"]
    ): boolean => {
      const modules: string[] = user?.company?.modules || [];
      const grantedPermissions = getGrantedPermissions(user?.permissions);
      if (!moduleNames.length || !grantedPermissions) {
        return false;
      }

      return moduleNames.some((moduleName) => {
        const permissionConfig = UI_MODULE_PERMISSIONS[moduleName];
        if (!permissionConfig || permissionConfig === "none") {
          return false;
        }

        const moduleAuthorized = modules.some(
          (userModuleName) =>
            userModuleName.match(new RegExp(moduleName, "i")) &&
            requireWorkspaces.some(
              (requireWorkspace) =>
                (requireWorkspace === "master" && isMasterContext()) ||
                (requireWorkspace === "operational" && isOperationalContext())
            )
        );
        if (!moduleAuthorized) return false;

        if (permissionConfig === "all") return true;

        const modulePermissions = extractModulePermissions(permissionConfig);
        if (!Array.isArray(modulePermissions) || !modulePermissions.length) {
          return false;
        }

        return grantedPermissions.some((userPermission) =>
          modulePermissions.some((modulePermission) =>
            matchesPermission(userPermission, modulePermission)
          )
        );
      });
    },
    [isMasterContext, isOperationalContext, user]
  );
  const hasPermission = useCallback(
    (...permissions: string[]): boolean => {
      return hasAnyLegacyPermission(user?.permissions, permissions);
    },
    [user]
  );
  const hasCapability = useCallback(
    (
      capability: string,
      options: { whenContractMissing?: CapabilityContractFallback } = {}
    ): boolean => {
      if (!user) return false;
      return resolveCapabilityAccess({
        userCapabilities: user.access_control?.capabilities,
        capabilities: [capability],
        whenContractMissing: options.whenContractMissing ?? "deny"
      });
    },
    [user]
  );

  /**
   * Variante con alcance operativo: exige la capability **y** que el workspace
   * activo este en `operational_workspace_ids`. `hasCapability` no mira scope,
   * asi que para gatear acciones sobre inventario —donde exponer unidades de
   * otro workspace seria el defecto— hace falta esta comprobacion extra.
   */
  const hasOperationalCapability = useCallback(
    (...capabilities: string[]): boolean => {
      return hasCanonicalOperationalCapability(
        user?.access_control,
        workspace?.id,
        ...capabilities
      );
    },
    [user, workspace?.id]
  );
  const hasPermissionToRender = useCallback(
    (...permissions: string[]) =>
      (
        apply: () => JSX.Element,
        otherwise: (() => JSX.Element) | null = null
      ): JSX.Element | null => {
        if (!hasPermission(...permissions)) {
          return otherwise ? otherwise() : null;
        }
        return apply();
      },
    [hasPermission]
  );
  const hasCapabilityToRender = useCallback(
    (capability: string) =>
      (
        apply: () => JSX.Element,
        otherwise: (() => JSX.Element) | null = null
      ): JSX.Element | null => {
        if (!hasCapability(capability)) {
          return otherwise ? otherwise() : null;
        }
        return apply();
      },
    [hasCapability]
  );

  return {
    user: user ?? undefined,
    workspace,
    workspaceContext,
    isModuleAuthorized,
    hasPermission,
    hasCapability,
    hasCapabilityToRender,
    hasOperationalCapability,
    hasPermissionToRender
  };
}
